import { useQuery } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import { formatUnits } from 'viem'
import { AAVE_V3_BASE_TOKENS } from '../data/tokens'
import type { Token } from '../types'

interface ReserveData {
  id: string
  underlyingAsset: string
  symbol: string
  name: string
  decimals: number
  liquidityRate: string
  variableBorrowRate: string
  totalLiquidity: string
  totalCurrentVariableDebt: string
  priceInEth: string
  priceInUsd: string
  isActive: boolean
  supplyAPY: number
  borrowAPY: number
  ltv: number
  isUsingFallbackData: boolean
}

interface AaveApiResponse {
  success: boolean
  data: ReserveData[]
  isUsingFallbackData: boolean
  timestamp: string
}

// Cache key for localStorage
const APY_CACHE_KEY = 'metadaave-apy-cache'

// Cache interface
interface ApyCache {
  data: ReserveData[]
  timestamp: number
  expiresAt: number
}

// Get cached APY data from localStorage
function getCachedApyData(): ReserveData[] | null {
  try {
    const cached = localStorage.getItem(APY_CACHE_KEY)
    if (!cached) return null
    
    const cache: ApyCache = JSON.parse(cached)
    const now = Date.now()
    
    // Check if cache is still valid (24 hours)
    if (now > cache.expiresAt) {
      console.log('[aave-data] Cache expired, removing')
      localStorage.removeItem(APY_CACHE_KEY)
      return null
    }
    
    console.log('[aave-data] Using cached APY data (age:', Math.round((now - cache.timestamp) / 1000 / 60), 'minutes)')
    return cache.data
  } catch (error) {
    console.error('[aave-data] Error reading cache:', error)
    return null
  }
}

// Save APY data to localStorage
function saveApyDataToCache(data: ReserveData[]): void {
  try {
    const now = Date.now()
    const cache: ApyCache = {
      data,
      timestamp: now,
      expiresAt: now + (24 * 60 * 60 * 1000) // 24 hours
    }
    
    localStorage.setItem(APY_CACHE_KEY, JSON.stringify(cache))
    console.log('[aave-data] Saved APY data to cache')
  } catch (error) {
    console.error('[aave-data] Error saving to cache:', error)
  }
}

// Fetch Aave V3 data from our Vercel API endpoint
async function fetchAaveReserveData(): Promise<ReserveData[]> {
  // Use Vercel API URL when running locally (including ngrok)
  const apiUrl = window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok')
    ? `${window.location.origin}/api/aave`
    : '/api/aave'
  
  console.log('[aave-data] Fetching from API:', apiUrl)
  
  try {
    const response = await fetch(apiUrl)
    console.log('[aave-data] Response status:', response.status)
    console.log('[aave-data] Response headers:', Object.fromEntries(response.headers.entries()))
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }
    
    const result: AaveApiResponse = await response.json()
    console.log('[aave-data] API result:', result)
    
    if (!result.success) {
      throw new Error('API returned error')
    }
    
    console.log('[aave-data] Successfully fetched', result.data.length, 'reserves')
    
    // Save to cache for next time
    saveApyDataToCache(result.data)
    
    return result.data
  } catch (error) {
    console.error('[aave-data] Error fetching Aave data:', error)
    throw error
  }
}

// Hook to get Aave V3 data from API with caching
export function useAaveData() {
  // Get cached data immediately
  const cachedData = getCachedApyData()
  
  const query = useQuery({
    queryKey: ['aave-data'],
    queryFn: fetchAaveReserveData,
    initialData: cachedData || undefined, // Use cached data as initial data
    staleTime: 300000, // 5 minutes - data is considered fresh for 5 minutes
    gcTime: 1800000, // 30 minutes - keep in cache longer
    refetchInterval: 600000, // Refetch every 10 minutes in background
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Prevent refetch on focus
    refetchOnMount: true, // Always refetch on mount to get fresh data
    refetchOnReconnect: true, // Refetch on network reconnect
    refetchIntervalInBackground: true, // Continue refetching in background
  })
  
  // Only log on state changes, not every render
  if (query.isLoading && !query.data) {
    console.log('[aave-data] Loading Aave data...')
  } else if (query.isError) {
    console.error('[aave-data] Error loading Aave data:', query.error)
  } else if (query.data && !query.isLoading) {
    console.log('[aave-data] Loaded', query.data.length, 'reserves')
  }
  
  return query
}

// Hook to get enriched token data with Aave APYs
export function useEnrichedTokens() {
  const { data: aaveData, isLoading, error } = useAaveData()

  const enrichedTokens: Token[] = AAVE_V3_BASE_TOKENS.map(token => {
    const reserveData = aaveData?.find(
      reserve => reserve.underlyingAsset.toLowerCase() === token.address.toLowerCase()
    )

    if (reserveData) {
      return {
        ...token,
        supplyAPY: reserveData.supplyAPY,
        borrowAPY: reserveData.borrowAPY,
        decimals: reserveData.decimals,
        apy: `${reserveData.supplyAPY.toFixed(2)}%`,
        priceInUsd: parseFloat(reserveData.priceInUsd),
      }
    }

    return token
  })

  // Check if we're using fallback data by looking at any reserve data
  const isUsingFallbackData = aaveData?.some(reserve => reserve.isUsingFallbackData) || false

  return {
    tokens: enrichedTokens,
    isLoading,
    error,
    isUsingFallbackData
  }
}